import React, { useState, useEffect, useMemo } from 'react';
import { B } from '../constants';
import { borderRadius, transitions } from '../designTokens';
import { promos as promosAPI, users as usersAPI } from '../api';
import ReelsFeed from '../components/ReelsFeed';
import PromoUploadModal from '../components/PromoUploadModal';
import Footer from '../components/Footer';
import Input from '../components/ui/Input';
import { useToast } from '../ToastContext';
import { trackEvent as trackFeedEvent, startFeedTracking, stopFeedTracking } from '../lib/feedEvents';
import TrendingSounds from '../components/TrendingSounds';
import HashtagChallenge from '../components/HashtagChallenge';
import CreatorSpotlight from '../components/CreatorSpotlight';
import ForYouFeed from '../components/ForYouFeed';
import ReelsPostButton from '../components/ReelsPostButton';
import CreatorComingSoon from '../components/CreatorComingSoon';
import './reelsTheme.css';
import UserSearchPanel from '../components/UserSearchPanel';

const Skeleton = ({ style }) => (
  <div style={{ background: 'rgba(15,25,35,0.06)', borderRadius: borderRadius.lg, animation: 'pulse 1.5s ease-in-out infinite', ...style }} />
);

export default function MockReelsScreen({ user, onAuthRequired, goScreen }) {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [followingIds, setFollowingIds] = useState([]);
  const [search, setSearch] = useState('');
  const [viewer, setViewer] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [preselectedSound, setPreselectedSound] = useState(null);
  const [activeTab, setActiveTab] = useState('feed');
  const showToast = useToast();

  const handlePostPromo = () => {
    if (user) setShowUpload(true);
    else onAuthRequired();
  };

  const fetchReels = async () => {
    setLoading(true);
    try {
      const data = await promosAPI.list({ feed: 'ranked' });
      setReels(data.promos || []);
    } catch (err) {
      console.error('Failed to fetch reels:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReels();
    startFeedTracking();
    return () => stopFeedTracking();
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!user) { if (mounted) setFollowingIds([]); return; }
      try {
        const data = await usersAPI.following(user.id);
        if (mounted) setFollowingIds(data.following.map(u => u.id));
      } catch { if (mounted) setFollowingIds([]); }
    };
    load();
    return () => { mounted = false; };
  }, [user]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media(max-width:640px){
        .reels{grid-template-columns:repeat(2,1fr)!important}
        .reels-heading{font-size:28px!important}
        .reels-post-btn{padding:10px 18px!important;font-size:13px!important}
        .reels-header{padding-top:24px!important}
        .reels-filter-row{flex-direction:column!important}
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const filtered = useMemo(() => reels.filter(p => {
    const matchFilter = filter === 'all'
      || (filter === 'influencer' && p.user?.isInfluencer)
      || (filter === 'user' && !p.user?.isInfluencer)
      || (filter === 'following' && followingIds.includes(p.user?.id));
    const matchSearch = !search
      || p.title?.toLowerCase().includes(search.toLowerCase())
      || p.destination?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  }), [reels, filter, followingIds, search]);

  const tabBar = (
    <div style={{ display: 'flex', gap: 4, padding: '12px clamp(16px, 4vw, 40px)', background: 'var(--reels-bar-bg)', borderBottom: '1px solid var(--reels-border)', overflowX: 'auto' }}>
      {[
        { id: 'feed', label: '📱 Feed' },
        { id: 'foryou', label: '🎯 For You' },
        { id: 'sounds', label: '🎵 Sounds' },
        { id: 'challenges', label: '🏆 Challenges' },
        { id: 'spotlight', label: '🌟 Creators' },
      ].map(tab => (
        <button key={tab.id} onClick={() => setActiveTab(tab.id)}
          style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: activeTab === tab.id ? 'var(--reels-accent-bg)' : 'transparent',
            color: activeTab === tab.id ? 'var(--reels-accent)' : 'var(--reels-muted)',
            cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', fontFamily: "'Montserrat',sans-serif",
          }}>
          {tab.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="reels-theme" style={{ background: B.sandPattern, minHeight: '100vh', position: 'relative', paddingBottom: 80 }}>
      {tabBar}

      {/* Header */}
      <div className="reels-header" style={{ maxWidth: 1200, margin: '0 auto', padding: '40px clamp(16px, 3vw, 40px)', position: 'relative' }}>
        <div style={{ marginBottom: 32 }}>
          <span style={{ fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700, color: B.forest, fontFamily: "'Montserrat',sans-serif" }}>Watch & get inspired</span>
          <h1 className="reels-heading" style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: 800, marginTop: 6 }}>Travel Reels</h1>
          <p style={{ color: B.muted, fontSize: 15, marginTop: 8 }}>Real moments from creators around the world</p>
        </div>

        <ReelsPostButton onClick={handlePostPromo} />

        <UserSearchPanel user={user} onAuthRequired={onAuthRequired} />

        {/* Search + filter pills */}
        <div className="reels-filter-row" style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16 }}>🔍</span>
            <Input type="text" placeholder="Search destination or title..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', padding: '10px 16px 10px 42px', borderRadius: 50, border: '1px solid #e5e0d8', background: 'white', fontFamily: "'Montserrat',sans-serif", fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', background: 'white', borderRadius: 50, padding: 3, gap: 2, border: '1px solid #e5e0d8' }}>
            {[['all', '🌍 All'], ['following', '👥 Following'], ['influencer', '⭐ Creators'], ['user', '👤 Travellers']].map(([id, label]) => (
              <button key={id} onClick={() => setFilter(id)}
                style={{
                  padding: '8px 16px', borderRadius: 50, border: 'none', cursor: 'pointer',
                  fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700,
                  background: filter === id ? B.forest : 'transparent',
                  color: filter === id ? 'white' : B.muted,
                  transition: `all ${transitions.fast}`,
                }}>
                {label}
              </button>
            ))}
          </div>
        </div>

      </div>

      {/* Premium discovery content */}
      {activeTab === 'foryou' && <ForYouFeed user={user} onPlay={(p, i) => setViewer({ promos: p, startIndex: i })} />}
      {activeTab === 'sounds' && (
        <div style={{ padding: '0 clamp(16px, 3vw, 40px) 32px' }}>
          <div style={{ background: 'var(--reels-panel-bg)', borderRadius: 12, border: '1px solid var(--reels-border)', padding: 20 }}>
            <TrendingSounds onSelect={(sound) => {
              setPreselectedSound(sound);
              setShowUpload(true);
              showToast(`🎵 Selected: ${sound.name} — now post your reel!`);
            }} />
          </div>
        </div>
      )}
      {activeTab === 'challenges' && (
        <div style={{ padding: '0 clamp(16px, 3vw, 40px) 32px' }}>
          <div style={{ background: 'var(--reels-panel-bg)', borderRadius: 12, border: '1px solid var(--reels-border)', padding: 20 }}>
            <HashtagChallenge onJoin={(challengeId) => {
              if (!user) { onAuthRequired(); return; }
              showToast('🏆 Joined challenge! Create a reel to enter.');
            }} />
          </div>
        </div>
      )}
      {activeTab === 'spotlight' && (
        <div style={{ padding: '0 clamp(16px, 3vw, 40px) 32px' }}>
          <div style={{ background: 'var(--reels-panel-bg)', borderRadius: 12, border: '1px solid var(--reels-border)', padding: 20 }}>
            <CreatorSpotlight />
            <CreatorComingSoon />
          </div>
        </div>
      )}

      {/* Feed grid */}
      {activeTab === 'feed' && (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(16px, 3vw, 40px) 40px' }}>
          {loading ? (
            <div className="reels" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} style={{ aspectRatio: '9/16' }} />
              ))}
            </div>
          ) : filter === 'following' && followingIds.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '70px 20px', color: B.muted }}>
              <div style={{ fontSize: 46, marginBottom: 12 }}>👥</div>
              <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22 }}>Follow someone to get interested reels/videos</h3>
              <p style={{ marginTop: 8, fontSize: 14 }}>Tap "Follow me" on a reel you like and their new posts land here.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '70px 20px', color: B.muted }}>
              <div style={{ fontSize: 46, marginBottom: 12 }}>🎬</div>
              <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22 }}>No reels yet</h3>
              <p style={{ marginTop: 8, fontSize: 14 }}>Be the first to share your travel story!</p>
              <button onClick={() => { user ? setShowUpload(true) : onAuthRequired(); }}
                style={{ marginTop: 20, background: B.forest, color: 'white', border: 'none', padding: '13px 30px', borderRadius: 50, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 700 }}>
                🎬 Post First Reel
              </button>
            </div>
          ) : (
            <div className="reels" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
              {filtered.map((reel, i) => (
                <div key={reel.id} className="reel"
                  onClick={() => {
                    setViewer({ promos: filtered, startIndex: i });
                    trackFeedEvent('impression', 'promo', reel.id);
                  }}
                  style={{
                    position: 'relative', aspectRatio: '9/16', borderRadius: borderRadius.lg,
                    overflow: 'hidden', cursor: 'pointer', boxShadow: '0 6px 20px rgba(15,25,35,0.10)',
                  }}>
                  <img src={reel.thumbnailUrl || reel.mediaUrl} alt={reel.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: `transform ${transitions.slow}` }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
                  <div className="play" style={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                    width: 54, height: 54, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(6px)',
                    display: 'grid', placeItems: 'center',
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z" /></svg>
                  </div>
                  <div className="ro" style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(180deg,rgba(0,0,0,0.1),rgba(0,0,0,0.65))',
                    display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: 16, color: 'white',
                  }}>
                    <div className="who" style={{ fontSize: 12, opacity: 0.85, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontFamily: "'Montserrat',sans-serif" }}>
                      👤 {reel.user?.name || reel.user}
                    </div>
                    <h4 style={{ fontFamily: "'Playfair Display',serif", fontSize: 14, fontWeight: 700, margin: 0 }}>{reel.title}</h4>
                    <div className="stats" style={{ fontSize: 12, opacity: 0.9, marginTop: 6, display: 'flex', gap: 12, fontFamily: "'Montserrat',sans-serif" }}>
                      <span>♥ {reel.likes || reel.likeCount || 0}</span>
                      <span>▶ {reel.views || reel.viewCount || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Footer goScreen={goScreen} />

      {/* Immersive vertical Reels player */}
      {viewer && (
        <ReelsFeed
          promos={viewer.promos}
          startIndex={viewer.startIndex}
          onClose={() => setViewer(null)}
          user={user}
          onDeleted={(id) => {
            setReels(prev => prev.filter(p => p.id !== id));
            setViewer(v => (v ? { ...v, promos: v.promos.filter(p => p.id !== id) } : v));
            showToast('Reel deleted');
          }}
          onAuthRequired={onAuthRequired}
          onPlanTrip={(p) => { setViewer(null); goScreen('ai', { prefillDestination: p.destination }); }}
          followingIds={followingIds}
          onFollowUpdate={() => user && usersAPI.following(user.id).then(d => setFollowingIds(d.following.map(u => u.id)))}
        />
      )}

      {/* Upload modal */}
      {showUpload && (
        <PromoUploadModal
          user={user}
          initialSound={preselectedSound}
          onClose={() => { setShowUpload(false); setPreselectedSound(null); }}
          onAuthRequired={onAuthRequired}
          onBatchCreated={() => { fetchReels(); showToast('📦 Batch posted! Your reels appear as they finish processing.'); }}
          onSuccess={(promo) => {
            setReels(prev => [promo, ...prev]);
            setShowUpload(false);
            setPreselectedSound(null);
            const statusMsg = promo.transcodingStatus && promo.transcodingStatus !== 'READY'
              ? ' ⏳ Video transcoding in progress...' : '';
            showToast('🎉 Promo published!' + statusMsg + ' Expires after ' + (promo.expireAfterDays || 3) + ' days.');
          }}
        />
      )}

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
    </div>
  );
}
